import {
  ENEMY_TYPE_CONFIG,
  GAME_HEIGHT,
  GAME_WIDTH,
  INITIAL_LIVES,
  INITIAL_PLAYER_X,
  INITIAL_PLAYER_Y,
  LEVELS,
  PLAYER_Y,
  POWERUP_TYPES,
  SPREAD_BULLET_VX,
} from './gameConfig';
import {
  getHazardSchedule as buildHazardSchedule,
  getWaveSchedule as buildWaveSchedule,
} from './levelPatterns';

export {
  ENEMY_TYPE_CONFIG,
  GAME_HEIGHT,
  GAME_WIDTH,
  INITIAL_LIVES,
  INITIAL_PLAYER_X,
  INITIAL_PLAYER_Y,
  LEVELS,
  PLAYER_Y,
  POWERUP_TYPES,
  SPREAD_BULLET_VX,
};

const PLAYER_SPEED = 270;
const PLAYER_VERTICAL_SPEED = 220;
const BULLET_SPEED = 520;
const POWERUP_SPEED = 105;
const POWERUP_SIZE = 30;
const POWERUP_DROP_PADDING = 42;
const POWERUP_MIN_SPACING = 72;
const PLAYER_WIDTH = 44;
const PLAYER_HEIGHT = 50;
const PLAYER_MIN_Y = 430;
const PLAYER_MAX_Y = PLAYER_Y;
const BULLET_WIDTH = 8;
const BULLET_HEIGHT = 30;
const ENEMY_WIDTH = 42;
const ENEMY_HEIGHT = 32;
const BOSS_WIDTH = 118;
const BOSS_HEIGHT = 78;
const BOSS_PROJECTILE_WIDTH = 14;
const BOSS_PROJECTILE_HEIGHT = 28;
const BOSS_PROJECTILE_SPEED = 245;
const HAZARD_WIDTH = 30;
const HAZARD_HEIGHT = 34;
const HAZARD_SPEED = 190;
const EXPLOSION_DURATION_MS = 420;
const MUZZLE_FLASH_DURATION_MS = 140;
const BOSS_HIT_FLASH_MS = 150;
const BOSS_INTRO_MS = 1300;
const PLAYER_INVULNERABLE_MS = 1500;
const POWERUP_DURATION_MS = 12000;
const OPTION_DRONE_DURATION_MS = 18000;
const DEFAULT_LEVEL_INDEX = 0;

export function getComboMultiplier(combo) {
  return Math.min(4, 1 + Math.floor(combo / 5));
}

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function getLevelConfig(levelIndex, levels = LEVELS) {
  return levels[clamp(levelIndex, 0, levels.length - 1)];
}

export function getSpacedPowerupX(preferredX, existingPowerups = []) {
  const offsets = [0, -82, 82, -142, 142];

  for (const offset of offsets) {
    const x = clamp(
      preferredX + offset,
      POWERUP_DROP_PADDING,
      GAME_WIDTH - POWERUP_DROP_PADDING
    );
    const hasSpacing = existingPowerups.every(
      (powerup) => Math.abs(powerup.x - x) >= POWERUP_MIN_SPACING
    );

    if (hasSpacing) {
      return x;
    }
  }

  return clamp(preferredX, POWERUP_DROP_PADDING, GAME_WIDTH - POWERUP_DROP_PADDING);
}

export function dropOneActivePowerup({
  rapidFireMs = 0,
  spreadShotMs = 0,
  optionDroneMs = 0,
}) {
  const activePowerups = [
    { key: 'rapidFireMs', remainingMs: rapidFireMs },
    { key: 'spreadShotMs', remainingMs: spreadShotMs },
    { key: 'optionDroneMs', remainingMs: optionDroneMs },
  ]
    .filter((powerup) => powerup.remainingMs > 0)
    .sort((a, b) => a.remainingMs - b.remainingMs);

  if (!activePowerups.length) {
    return { rapidFireMs, spreadShotMs, optionDroneMs };
  }

  return {
    rapidFireMs,
    spreadShotMs,
    optionDroneMs,
    [activePowerups[0].key]: 0,
  };
}

export function createInitialGameState(overrides = {}) {
  const levelIndex = overrides.levelIndex ?? DEFAULT_LEVEL_INDEX;
  const levels = overrides.levels ?? LEVELS;
  const level = getLevelConfig(levelIndex, levels);

  return {
    status: 'ready',
    score: 0,
    combo: 0,
    comboMultiplier: 1,
    levelIndex,
    levelName: level.name,
    levelEnemyCount: level.enemyCount,
    levelGoal: level.boss ? 'boss' : level.goal ?? 'clear',
    levelDurationMs: level.durationMs ?? null,
    levelElapsedMs: 0,
    enemiesSpawned: 0,
    hazardsSpawned: 0,
    enemiesDefeated: 0,
    lives: INITIAL_LIVES,
    playerInvulnerableMs: 0,
    playerX: INITIAL_PLAYER_X,
    playerY: INITIAL_PLAYER_Y,
    playerLean: 'center',
    rapidFireMs: 0,
    spreadShotMs: 0,
    optionDroneMs: 0,
    bullets: [],
    enemies: [],
    hazards: [],
    powerups: [],
    boss: null,
    bossProjectiles: [],
    bossWarningMs: 0,
    explosions: [],
    muzzleFlashes: [],
    challengeStats: null,
    challengeSummary: null,
    runStats: {
      sectorsCleared: 0,
      powerupsCollected: 0,
      noDamageSectors: 0,
    },
    runSummary: null,
    sectorTookDamage: false,
    practiceMode: false,
    ...overrides,
  };
}

export function createLevelStartState(levelIndex, score = 0, carriedEffects = {}, overrides = {}) {
  const levels = overrides.levels ?? LEVELS;
  const level = getLevelConfig(levelIndex, levels);
  const boss = level.boss ? createBoss(level.boss) : null;

  return createInitialGameState({
    status: 'playing',
    score,
    levelIndex,
    levelName: level.name,
    levelEnemyCount: level.enemyCount,
    levelGoal: level.boss ? 'boss' : level.goal ?? 'clear',
    levelDurationMs: level.durationMs ?? null,
    boss,
    bossWarningMs: boss ? BOSS_INTRO_MS : 0,
    rapidFireMs: carriedEffects.rapidFireMs ?? 0,
    spreadShotMs: carriedEffects.spreadShotMs ?? 0,
    optionDroneMs: carriedEffects.optionDroneMs ?? 0,
    runStats: carriedEffects.runStats ?? {
      sectorsCleared: 0,
      powerupsCollected: 0,
      noDamageSectors: 0,
    },
    challengeStats: level.goal === 'challenge'
      ? { hits: 0, misses: 0, escaped: 0, dropsCollected: 0, bonusScore: 0 }
      : null,
    challengeSummary: null,
    sectorTookDamage: false,
    practiceMode: overrides.practiceMode ?? false,
    ...overrides,
  });
}

export function createBoss(config = LEVELS[LEVELS.length - 1].boss) {
  return {
    id: config.id ?? 'boss-dread-core',
    name: config.name ?? 'Dread Core',
    x: GAME_WIDTH / 2,
    y: 88,
    baseY: 88,
    health: config.health ?? 32,
    maxHealth: config.health ?? 32,
    points: config.points ?? 500,
    phase: 0,
    direction: 1,
    speed: config.speed ?? 74,
    attackIntervalMs: config.attackIntervalMs ?? 980,
    attackTimerMs: config.attackIntervalMs ?? 980,
    summonIntervalMs: config.summonIntervalMs ?? 4200,
    summonTimerMs: config.summonIntervalMs ?? 4200,
    hitFlashMs: 0,
    phaseThresholds: config.phaseThresholds ?? [0.65, 0.3],
  };
}

export function createEnemy(id, level = getLevelConfig(DEFAULT_LEVEL_INDEX), random = Math.random) {
  if (typeof level === 'function') {
    random = level;
    level = getLevelConfig(DEFAULT_LEVEL_INDEX);
  }

  const lanePadding = 38;
  const enemyTypes = level.enemyTypes?.length ? level.enemyTypes : ['scout'];
  const type = enemyTypes[Math.floor(random() * enemyTypes.length)] ?? enemyTypes[0];
  const typeConfig = ENEMY_TYPE_CONFIG[type] ?? ENEMY_TYPE_CONFIG.scout;
  const x = lanePadding + random() * (GAME_WIDTH - lanePadding * 2);

  return {
    id,
    type,
    movement: typeConfig.movement ?? level.movement ?? 'straight',
    speed: (level.speed ?? 96) * (typeConfig.speedMultiplier ?? 1),
    health: typeConfig.health,
    maxHealth: typeConfig.health,
    points: typeConfig.points,
    baseX: x,
    drift: random() < 0.5 ? -1 : 1,
    phase: random() * Math.PI * 2,
    hazardTimerMs: typeConfig.hazardIntervalMs ?? null,
    lean: 'center',
    x,
    y: -28,
  };
}

function createEnemyFromWave(id, level, spec = {}) {
  const type = spec.type ?? level.enemyTypes?.[0] ?? 'scout';
  const typeConfig = ENEMY_TYPE_CONFIG[type] ?? ENEMY_TYPE_CONFIG.scout;
  const x = clamp(spec.x ?? GAME_WIDTH / 2, ENEMY_WIDTH / 2, GAME_WIDTH - ENEMY_WIDTH / 2);
  const speed = spec.speed ?? (level.speed ?? 96) * (typeConfig.speedMultiplier ?? 1);

  return {
    id,
    type,
    movement: spec.movement ?? typeConfig.movement ?? level.movement ?? 'straight',
    speed,
    health: spec.health ?? typeConfig.health,
    maxHealth: spec.health ?? typeConfig.health,
    points: spec.points ?? typeConfig.points,
    baseX: spec.baseX ?? x,
    drift: spec.drift ?? 1,
    phase: spec.phase ?? 0,
    hazardTimerMs: typeConfig.hazardIntervalMs ?? null,
    lean: 'center',
    x,
    y: spec.y ?? -28,
  };
}

export function getWaveSchedule(level = getLevelConfig(DEFAULT_LEVEL_INDEX)) {
  return buildWaveSchedule(level);
}

export function getDueWaveEnemies({
  level,
  elapsedMs,
  enemiesSpawned,
  nextEnemyId,
}) {
  const schedule = getWaveSchedule(level);

  return schedule
    .slice(enemiesSpawned)
    .filter((entry) => entry.atMs <= elapsedMs)
    .map((entry, index) => createEnemyFromWave(nextEnemyId + index, level, entry));
}

export function getHazardSchedule(level = getLevelConfig(DEFAULT_LEVEL_INDEX)) {
  return buildHazardSchedule(level);
}

export function getDueHazards({
  level,
  elapsedMs,
  hazardsSpawned = 0,
  nextHazardId,
}) {
  const schedule = getHazardSchedule(level);

  return schedule
    .slice(hazardsSpawned)
    .filter((entry) => entry.atMs <= elapsedMs)
    .map((entry, index) => ({
      id: `hazard-${nextHazardId + index}`,
      ...entry,
      ageMs: 0,
      vy: entry.type === 'meteorLane' ? HAZARD_SPEED : 0,
    }));
}

export function rectsOverlap(a, b) {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

function centeredRect(entity, width, height) {
  return {
    x: entity.x - width / 2,
    y: entity.y - height / 2,
    width,
    height,
  };
}

function moveEnemy(enemy, deltaSeconds) {
  const nextY = enemy.y + (enemy.speed ?? 96) * deltaSeconds;

  if (enemy.movement === 'drift') {
    const nextX = clamp(
      enemy.x + (enemy.drift ?? 1) * 38 * deltaSeconds,
      ENEMY_WIDTH / 2,
      GAME_WIDTH - ENEMY_WIDTH / 2
    );

    return {
      ...enemy,
      lean: enemy.drift < 0 ? 'left' : 'right',
      x: nextX,
      y: nextY,
    };
  }

  if (enemy.movement === 'sine') {
    const nextX = clamp(
      (enemy.baseX ?? enemy.x) + Math.sin(nextY / 44 + (enemy.phase ?? 0)) * 42,
      ENEMY_WIDTH / 2,
      GAME_WIDTH - ENEMY_WIDTH / 2
    );
    const horizontalDelta = nextX - enemy.x;

    return {
      ...enemy,
      lean: horizontalDelta < -0.1 ? 'left' : horizontalDelta > 0.1 ? 'right' : 'center',
      x: nextX,
      y: nextY,
    };
  }

  return {
    ...enemy,
    lean: 'center',
    y: nextY,
  };
}

function moveBoss(boss, deltaMs) {
  if (!boss) {
    return null;
  }

  const deltaSeconds = deltaMs / 1000;
  const healthRatio = (boss.health ?? 1) / (boss.maxHealth ?? boss.health ?? 1);
  const thresholds = boss.phaseThresholds ?? [0.65, 0.3];
  const attackPhase = healthRatio <= thresholds[1] ? 2 : healthRatio <= thresholds[0] ? 1 : 0;
  const phaseSpeedMultiplier = 1 + attackPhase * 0.16;
  const nextPhase = (boss.phase ?? 0) + deltaSeconds;
  let direction = boss.direction ?? 1;
  let nextX = boss.x + direction * (boss.speed ?? 74) * phaseSpeedMultiplier * deltaSeconds;
  const minX = BOSS_WIDTH / 2 + 12;
  const maxX = GAME_WIDTH - BOSS_WIDTH / 2 - 12;
  const wobbleX = Math.sin(nextPhase * (2.6 + attackPhase * 0.45)) * (16 + attackPhase * 7);
  const verticalWave = Math.sin(nextPhase * (2.2 + attackPhase * 0.35)) * (12 + attackPhase * 8);

  if (nextX <= minX || nextX >= maxX) {
    direction *= -1;
    nextX = clamp(nextX, minX, maxX);
  }

  return {
    ...boss,
    x: clamp(nextX + wobbleX * deltaSeconds * 5, minX, maxX),
    y: (boss.baseY ?? 88) + verticalWave + Math.sin(nextPhase * 4.1) * attackPhase * 6,
    phase: nextPhase,
    attackPhase,
    direction,
    attackTimerMs: Math.max(0, (boss.attackTimerMs ?? boss.attackIntervalMs ?? 980) - deltaMs),
    summonTimerMs: Math.max(0, (boss.summonTimerMs ?? boss.summonIntervalMs ?? 4200) - deltaMs),
    hitFlashMs: Math.max(0, (boss.hitFlashMs ?? 0) - deltaMs),
  };
}

function createBossProjectiles(boss) {
  const spread = boss.attackPhase >= 2 ? 48 : 36;
  const phaseKey = Math.round((boss.phase ?? 0) * 1000);
  const offsets = boss.attackPhase >= 1
    ? [-spread, -spread / 2, 0, spread / 2, spread]
    : [-spread, 0, spread];

  return offsets.map((offset, index) => ({
    id: `boss-shot-${phaseKey}-${index}`,
    x: boss.x + offset,
    y: boss.y + BOSS_HEIGHT / 2 - 6,
    vy: BOSS_PROJECTILE_SPEED + (boss.attackPhase ?? 0) * 28,
  }));
}

export function updateGame(
  game,
  {
    deltaMs,
    input,
    spawnedEnemies = [],
    spawnedHazards = [],
    newBullet = null,
    newBullets = null,
    levels = LEVELS,
  }
) {
  if (game.status !== 'playing') {
    return game;
  }

  const deltaSeconds = deltaMs / 1000;
  const direction = Number(Boolean(input.right)) - Number(Boolean(input.left));
  const verticalDirection = Number(Boolean(input.down)) - Number(Boolean(input.up));
  const playerLean = direction < 0 ? 'left' : direction > 0 ? 'right' : 'center';
  const playerInvulnerableMs = Math.max(
    0,
    (game.playerInvulnerableMs ?? 0) - deltaMs
  );
  let rapidFireMs = Math.max(0, (game.rapidFireMs ?? 0) - deltaMs);
  let spreadShotMs = Math.max(0, (game.spreadShotMs ?? 0) - deltaMs);
  let optionDroneMs = Math.max(0, (game.optionDroneMs ?? 0) - deltaMs);
  const levelElapsedMs = (game.levelElapsedMs ?? 0) + deltaMs;
  const bossWarningMs = Math.max(0, (game.bossWarningMs ?? 0) - deltaMs);
  const playerMinX = PLAYER_WIDTH / 2;
  const playerMaxX = GAME_WIDTH - PLAYER_WIDTH / 2;
  const playerX = clamp(
    game.playerX + direction * PLAYER_SPEED * deltaSeconds,
    playerMinX,
    playerMaxX
  );
  const playerY = clamp(
    (game.playerY ?? INITIAL_PLAYER_Y) + verticalDirection * PLAYER_VERTICAL_SPEED * deltaSeconds,
    PLAYER_MIN_Y,
    PLAYER_MAX_Y
  );

  const firedBullets = newBullets ?? (newBullet ? [newBullet] : []);
  const movedBullets = game.bullets
    .concat(firedBullets)
    .map((bullet) => ({
      ...bullet,
      x: bullet.x + (bullet.vx ?? 0) * deltaSeconds,
      y: bullet.y - BULLET_SPEED * deltaSeconds,
    }));
  const missedShot = movedBullets.some(
    (bullet) =>
      bullet.y <= -BULLET_HEIGHT ||
      bullet.x < -BULLET_WIDTH ||
      bullet.x > GAME_WIDTH + BULLET_WIDTH
  );
  let bullets = movedBullets.filter(
    (bullet) =>
      bullet.y > -BULLET_HEIGHT &&
      bullet.x > -BULLET_WIDTH &&
      bullet.x < GAME_WIDTH + BULLET_WIDTH
  );

  let enemies = game.enemies
    .concat(spawnedEnemies)
    .map((enemy) => moveEnemy(enemy, deltaSeconds));
  const bomberHazards = [];
  enemies = enemies.map((enemy) => {
    if (enemy.type !== 'bomber' || enemy.y < 20) {
      return enemy;
    }

    const timer = (enemy.hazardTimerMs ?? ENEMY_TYPE_CONFIG.bomber.hazardIntervalMs) - deltaMs;

    if (timer > 0) {
      return { ...enemy, hazardTimerMs: timer };
    }

    bomberHazards.push({
      id: `bomber-hazard-${enemy.id}-${Math.round(levelElapsedMs)}`,
      type: 'meteorLane',
      x: enemy.x,
      y: enemy.y + ENEMY_HEIGHT / 2,
      ageMs: 0,
      vy: HAZARD_SPEED * 0.86,
    });

    return {
      ...enemy,
      hazardTimerMs: ENEMY_TYPE_CONFIG.bomber.hazardIntervalMs,
    };
  });
  let hazards = (game.hazards || [])
    .concat(spawnedHazards)
    .concat(bomberHazards)
    .map((hazard) => ({
      ...hazard,
      ageMs: (hazard.ageMs ?? 0) + deltaMs,
      y: hazard.y + (hazard.vy ?? 0) * deltaSeconds,
    }))
    .filter((hazard) => (
      hazard.type === 'shieldGate'
        ? (hazard.ageMs ?? 0) < (hazard.durationMs ?? 1400)
        : hazard.y < GAME_HEIGHT + HAZARD_HEIGHT
    ));
  let boss = moveBoss(game.boss, deltaMs);
  let bossSummons = [];
  let bossProjectiles = (game.bossProjectiles || [])
    .map((projectile) => ({
      ...projectile,
      y: projectile.y + (projectile.vy ?? BOSS_PROJECTILE_SPEED) * deltaSeconds,
    }))
    .filter((projectile) => projectile.y < GAME_HEIGHT + BOSS_PROJECTILE_HEIGHT);

  if (boss && bossWarningMs <= 0 && boss.attackTimerMs <= 0) {
    bossProjectiles = bossProjectiles.concat(createBossProjectiles(boss));
    boss = {
      ...boss,
      attackTimerMs: (boss.attackIntervalMs ?? 980) - (boss.attackPhase ?? 0) * 150,
    };
  }

  if (boss && bossWarningMs <= 0 && boss.summonTimerMs <= 0) {
    const summonY = boss.y + BOSS_HEIGHT / 2;
    const summonSpeed = 130 + (boss.attackPhase ?? 0) * 22;
    bossSummons = [-1, 1].map((drift, index) => createEnemyFromWave(
      `boss-minion-${Math.round(levelElapsedMs)}-${index}`,
      {
        ...getLevelConfig(game.levelIndex, levels),
        enemyTypes: ['raider'],
        movement: 'drift',
        speed: summonSpeed,
      },
      {
        type: 'raider',
        health: 2 + (boss.attackPhase ?? 0),
        points: 25,
        x: clamp(boss.x + drift * 72, ENEMY_WIDTH / 2, GAME_WIDTH - ENEMY_WIDTH / 2),
        y: summonY,
        baseX: boss.x,
        drift,
        movement: (boss.attackPhase ?? 0) >= 1 ? 'sine' : 'drift',
        speed: summonSpeed,
      }
    ));
    boss = {
      ...boss,
      summonTimerMs: Math.max(1800, (boss.summonIntervalMs ?? 4200) - (boss.attackPhase ?? 0) * 520),
    };
  }

  const destroyedBulletIds = new Set();
  const destroyedEnemyIds = new Set();
  const destroyedBossProjectileIds = new Set();
  const newExplosions = [];
  let score = game.score;
  let combo = game.combo ?? 0;
  let comboMultiplier = game.comboMultiplier ?? getComboMultiplier(combo);
  const enemiesSpawned = game.enemiesSpawned + spawnedEnemies.length;
  const hazardsSpawned = (game.hazardsSpawned ?? 0) + spawnedHazards.length;
  let enemiesDefeated = game.enemiesDefeated;
  const newPowerups = [];
  let challengeStats = game.challengeStats ? { ...game.challengeStats } : null;
  let runStats = {
    sectorsCleared: 0,
    powerupsCollected: 0,
    noDamageSectors: 0,
    ...(game.runStats ?? {}),
  };
  const splitEnemies = [];

  bullets.forEach((bullet) => {
    if (destroyedBulletIds.has(bullet.id)) {
      return;
    }

    const bulletRect = centeredRect(bullet, BULLET_WIDTH, BULLET_HEIGHT);
    const hitEnemy = enemies.find((enemy) => {
      if (destroyedEnemyIds.has(enemy.id)) {
        return false;
      }

      return rectsOverlap(bulletRect, centeredRect(enemy, ENEMY_WIDTH, ENEMY_HEIGHT));
    });

    if (hitEnemy) {
      const nextHealth = (hitEnemy.health ?? 1) - 1;
      if (!bullet.piercing) {
        destroyedBulletIds.add(bullet.id);
      }
      enemies = enemies.map((enemy) =>
        enemy.id === hitEnemy.id
          ? { ...enemy, health: nextHealth }
          : enemy
      );

      if (nextHealth <= 0) {
        destroyedEnemyIds.add(hitEnemy.id);
        newExplosions.push({
          id: `${hitEnemy.id}-${bullet.id}`,
          x: hitEnemy.x,
          y: hitEnemy.y,
          ageMs: 0,
        });
        const nextEnemiesDefeated = enemiesDefeated + 1;
        enemiesDefeated = nextEnemiesDefeated;
        if (challengeStats) {
          challengeStats.hits += 1;
        }
        combo += 1;
        comboMultiplier = getComboMultiplier(combo);
        score += (hitEnemy.points ?? 10) * comboMultiplier;

        const currentLevel = getLevelConfig(game.levelIndex, levels);
        const powerupEvery = currentLevel.powerupEvery ?? 4;

        if (nextEnemiesDefeated % powerupEvery === 0) {
          const powerupIndex = (nextEnemiesDefeated / powerupEvery - 1) % POWERUP_TYPES.length;
          newPowerups.push({
            id: `powerup-${hitEnemy.id}-${nextEnemiesDefeated}`,
            type: POWERUP_TYPES[powerupIndex],
            x: getSpacedPowerupX(hitEnemy.x, game.powerups.concat(newPowerups)),
            y: hitEnemy.y,
          });
        }

        if ((ENEMY_TYPE_CONFIG[hitEnemy.type]?.splitInto ?? 0) > 0) {
          [-1, 1].forEach((drift, splitIndex) => {
            splitEnemies.push(createEnemyFromWave(
              `${hitEnemy.id}-split-${splitIndex}`,
              {
                ...currentLevel,
                enemyTypes: ['splinter'],
                speed: (currentLevel.speed ?? 96) + 16,
                movement: 'drift',
              },
              {
                type: 'splinter',
                x: hitEnemy.x + drift * 18,
                y: hitEnemy.y,
                baseX: hitEnemy.x,
                drift,
                movement: 'drift',
              }
            ));
          });
        }
      }

      return;
    }

    if (boss && rectsOverlap(bulletRect, centeredRect(boss, BOSS_WIDTH, BOSS_HEIGHT))) {
      const nextHealth = (boss.health ?? 1) - 1;
      destroyedBulletIds.add(bullet.id);

      if (nextHealth <= 0) {
        combo += 1;
        comboMultiplier = getComboMultiplier(combo);
        score += (boss.points ?? 500) * comboMultiplier;
        newExplosions.push({
          id: `boss-defeat-${bullet.id}`,
          x: boss.x,
          y: boss.y,
          ageMs: 0,
          kind: 'boss',
        });
        boss = null;
      } else {
        boss = {
          ...boss,
          health: nextHealth,
          hitFlashMs: BOSS_HIT_FLASH_MS,
        };
      }
    }
  });

  bullets = bullets.filter((bullet) => !destroyedBulletIds.has(bullet.id));
  const muzzleFlashes = (game.muzzleFlashes || [])
    .map((muzzleFlash) => ({
      ...muzzleFlash,
      ageMs: muzzleFlash.ageMs + deltaMs,
    }))
    .filter((muzzleFlash) => muzzleFlash.ageMs < MUZZLE_FLASH_DURATION_MS)
    .concat(
      firedBullets.map((bullet) => ({
            id: `flash-${bullet.id}`,
            x: bullet.x,
            y: playerY - 42,
            ageMs: 0,
          }))
    );
  let powerups = (game.powerups || [])
    .concat(newPowerups)
    .map((powerup) => ({
      ...powerup,
      y: powerup.y + POWERUP_SPEED * deltaSeconds,
    }))
    .filter((powerup) => powerup.y < GAME_HEIGHT + POWERUP_SIZE);
  let remainingEnemies = enemies
    .filter((enemy) => !destroyedEnemyIds.has(enemy.id))
    .concat(splitEnemies)
    .concat(bossSummons);
  const playerRect = centeredRect({ x: playerX, y: playerY }, PLAYER_WIDTH, PLAYER_HEIGHT);
  const collectedPowerupIds = new Set();
  const damagingEnemyIds = new Set();
  const shieldGateActive = hazards.some(
    (hazard) =>
      hazard.type === 'shieldGate' &&
      Math.abs(hazard.x - playerX) <= 34 &&
      Math.abs(hazard.y - playerY) <= 150
  );

  remainingEnemies.forEach((enemy) => {
    if (
      game.levelGoal !== 'challenge' &&
      rectsOverlap(playerRect, centeredRect(enemy, ENEMY_WIDTH, ENEMY_HEIGHT))
    ) {
      damagingEnemyIds.add(enemy.id);
    }

    if (enemy.y > GAME_HEIGHT + ENEMY_HEIGHT && game.levelGoal !== 'challenge') {
      damagingEnemyIds.add(enemy.id);
    }
  });

  const escapedChallengeEnemies = game.levelGoal === 'challenge'
    ? remainingEnemies.filter((enemy) => enemy.y > GAME_HEIGHT + ENEMY_HEIGHT)
    : [];

  if (challengeStats && escapedChallengeEnemies.length) {
    challengeStats.escaped += escapedChallengeEnemies.length;
    challengeStats.misses += escapedChallengeEnemies.length;
  }

  if (boss && rectsOverlap(playerRect, centeredRect(boss, BOSS_WIDTH * 0.78, BOSS_HEIGHT * 0.72))) {
    damagingEnemyIds.add(boss.id);
  }

  bossProjectiles.forEach((projectile) => {
    if (rectsOverlap(
      playerRect,
      centeredRect(projectile, BOSS_PROJECTILE_WIDTH, BOSS_PROJECTILE_HEIGHT)
    )) {
      damagingEnemyIds.add(projectile.id);
      destroyedBossProjectileIds.add(projectile.id);
    }
  });

  hazards.forEach((hazard) => {
    if (
      hazard.type === 'meteorLane' &&
      !shieldGateActive &&
      rectsOverlap(playerRect, centeredRect(hazard, HAZARD_WIDTH, HAZARD_HEIGHT))
    ) {
      damagingEnemyIds.add(hazard.id);
    }
  });

  const playerDamaged = damagingEnemyIds.size > 0 && playerInvulnerableMs <= 0;
  const lives = playerDamaged ? Math.max(0, (game.lives ?? INITIAL_LIVES) - 1) : game.lives ?? INITIAL_LIVES;
  const nextPlayerInvulnerableMs = playerDamaged && lives > 0
    ? PLAYER_INVULNERABLE_MS
    : playerInvulnerableMs;
  let nextLives = lives;
  let scoreWithPowerups = score;

  if (playerDamaged && lives > 0) {
    remainingEnemies = remainingEnemies.filter((enemy) => !damagingEnemyIds.has(enemy.id));
    damagingEnemyIds.forEach((enemyId) => {
      const damagedEnemy = enemies.find((enemy) => enemy.id === enemyId);
      const damagedProjectile = bossProjectiles.find((projectile) => projectile.id === enemyId);
      const damagedHazard = hazards.find((hazard) => hazard.id === enemyId);

      if (damagedEnemy) {
        newExplosions.push({
          id: `damage-${damagedEnemy.id}-${Math.round(damagedEnemy.y)}`,
          x: damagedEnemy.x,
          y: damagedEnemy.y,
          ageMs: 0,
        });
      }

      if (damagedProjectile) {
        newExplosions.push({
          id: `damage-${damagedProjectile.id}`,
          x: damagedProjectile.x,
          y: damagedProjectile.y,
          ageMs: 0,
        });
      }

      if (damagedHazard) {
        newExplosions.push({
          id: `damage-${damagedHazard.id}`,
          x: damagedHazard.x,
          y: damagedHazard.y,
          ageMs: 0,
        });
      }
    });
  }

  if (playerDamaged) {
    bossProjectiles = bossProjectiles.filter(
      (projectile) => !destroyedBossProjectileIds.has(projectile.id)
    );
    hazards = hazards.filter((hazard) => !damagingEnemyIds.has(hazard.id));
  }

  if (missedShot || playerDamaged) {
    combo = 0;
    comboMultiplier = 1;
  }

  if (missedShot && challengeStats) {
    challengeStats.misses += 1;
  }

  if (playerDamaged) {
    const nextPowerupTimers = dropOneActivePowerup({
      rapidFireMs,
      spreadShotMs,
      optionDroneMs,
    });
    rapidFireMs = nextPowerupTimers.rapidFireMs;
    spreadShotMs = nextPowerupTimers.spreadShotMs;
    optionDroneMs = nextPowerupTimers.optionDroneMs;
  }

  powerups.forEach((powerup) => {
    if (!rectsOverlap(playerRect, centeredRect(powerup, POWERUP_SIZE, POWERUP_SIZE))) {
      return;
    }

    collectedPowerupIds.add(powerup.id);
    runStats.powerupsCollected += 1;
    if (challengeStats) {
      challengeStats.dropsCollected += 1;
    }

    if (powerup.type === 'rapidFire') {
      rapidFireMs = POWERUP_DURATION_MS;
    }

    if (powerup.type === 'spreadShot') {
      spreadShotMs = POWERUP_DURATION_MS;
    }

    if (powerup.type === 'optionDrone') {
      optionDroneMs = OPTION_DRONE_DURATION_MS;
    }

    if (powerup.type === 'pulseBomb') {
      const clearedEnemies = remainingEnemies.filter((enemy) => enemy.type !== 'heavy');
      clearedEnemies.forEach((enemy) => {
        newExplosions.push({
          id: `pulse-${enemy.id}`,
          x: enemy.x,
          y: enemy.y,
          ageMs: 0,
        });
      });
      scoreWithPowerups += clearedEnemies.length * 15 * comboMultiplier;
      enemiesDefeated += clearedEnemies.length;
      remainingEnemies = remainingEnemies.filter((enemy) => enemy.type === 'heavy');
      bossProjectiles = [];
      hazards = hazards.filter((hazard) => hazard.type === 'shieldGate');
    }

    if (powerup.type === 'shield') {
      nextLives = Math.min(INITIAL_LIVES, nextLives + 1);
    }

    if (powerup.type === 'bonus') {
      scoreWithPowerups += 100 * comboMultiplier;
    }
  });

  powerups = powerups.filter((powerup) => !collectedPowerupIds.has(powerup.id));

  const explosions = (game.explosions || [])
    .map((explosion) => ({
      ...explosion,
      ageMs: explosion.ageMs + deltaMs,
    }))
    .filter((explosion) => explosion.ageMs < EXPLOSION_DURATION_MS)
    .concat(newExplosions);

  const enemiesInBounds = remainingEnemies.filter(
    (enemy) => enemy.y < GAME_HEIGHT + ENEMY_HEIGHT
  );
  const levelCleared = game.levelGoal === 'boss'
    ? !boss
    : game.levelGoal === 'survive'
    ? levelElapsedMs >= (game.levelDurationMs ?? 0)
    : game.levelGoal === 'challenge'
    ? enemiesSpawned >= game.levelEnemyCount &&
      enemiesInBounds.length === 0
    : enemiesSpawned >= game.levelEnemyCount &&
      enemiesInBounds.length === 0;
  const allLevelsCleared = levelCleared && game.levelIndex >= levels.length - 1;
  const currentLevel = getLevelConfig(game.levelIndex, levels);
  let challengeSummary = game.challengeSummary;
  let runSummary = game.runSummary;

  if (levelCleared && game.status === 'playing') {
    const noDamageClear = !playerDamaged && !(game.sectorTookDamage ?? false);
    runStats = {
      ...runStats,
      sectorsCleared: runStats.sectorsCleared + 1,
      noDamageSectors: noDamageClear ? runStats.noDamageSectors + 1 : runStats.noDamageSectors,
    };

    if (challengeStats) {
      const perfectClear = challengeStats.misses === 0 && enemiesDefeated >= game.levelEnemyCount;
      const fastClear = levelElapsedMs <= (currentLevel.fastClearMs ?? 0);
      const bonusScore = (perfectClear ? currentLevel.perfectClearBonus ?? 0 : 0) +
        (fastClear ? currentLevel.fastClearBonus ?? 0 : 0);

      challengeStats.bonusScore = bonusScore;
      scoreWithPowerups += bonusScore;
      challengeSummary = {
        hits: challengeStats.hits,
        misses: challengeStats.misses,
        escaped: challengeStats.escaped,
        dropsCollected: challengeStats.dropsCollected,
        perfectClear,
        fastClear,
        bonusScore,
      };
    }
  }

  const nextStatus = playerDamaged && lives <= 0
    ? 'gameOver'
    : allLevelsCleared
    ? 'won'
    : levelCleared
    ? 'levelComplete'
    : 'playing';

  if (['gameOver', 'won'].includes(nextStatus)) {
    runSummary = {
      score: scoreWithPowerups,
      bestScore: Math.max(scoreWithPowerups, game.runSummary?.bestScore ?? 0),
      sectorsCleared: runStats.sectorsCleared,
      powerupsCollected: runStats.powerupsCollected,
      noDamageSectors: runStats.noDamageSectors,
      victory: nextStatus === 'won',
    };
  }

  return {
    ...game,
    status: nextStatus,
    score: scoreWithPowerups,
    combo,
    comboMultiplier,
    enemiesSpawned,
    hazardsSpawned,
    enemiesDefeated,
    levelElapsedMs,
    bossWarningMs,
    lives: nextLives,
    playerInvulnerableMs: nextPlayerInvulnerableMs,
    rapidFireMs,
    spreadShotMs,
    optionDroneMs,
    playerX,
    playerY,
    playerLean,
    bullets,
    powerups,
    boss,
    bossProjectiles,
    hazards,
    explosions,
    muzzleFlashes,
    enemies: enemiesInBounds,
    challengeStats,
    challengeSummary,
    runStats,
    runSummary,
    sectorTookDamage: (game.sectorTookDamage ?? false) || playerDamaged,
  };
}
